import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import KUDOS_TYPES, { getKudosTypeById } from '../../../constants/kudosTypes';
import KudoDescriptionBubble from '../../../components/KudoDescriptionBubble';

const KudosSection = ({ activeTab, userId }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [kudos, setKudos] = useState([]);
  const [kudosByType, setKudosByType] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedKudos, setExpandedKudos] = useState(null);
  const [flaggingId, setFlaggingId] = useState(null);
  const [eventFilter, setEventFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [hoveredKudosType, setHoveredKudosType] = useState(null);
  const [isCelebrating, setIsCelebrating] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState(null);
  const [animatedCounts, setAnimatedCounts] = useState({});
  const [highlightedTypes, setHighlightedTypes] = useState(new Set());
  const celebrationTimerRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  const getCurrentTab = () => {
    const tabPart = activeTab.split('-')[1];
    return tabPart || 'received';
  };

  useEffect(() => {
    fetchKudos();
  }, [activeTab, userId]);

  const fetchKudos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const tab = getCurrentTab();
      const url = tab === 'received'
        ? `${API_BASE}/user-kudos/received`
        : `${API_BASE}/user-kudos/given`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setKudos(data.data || []);
        setKudosByType(data.kudosByType || {});
      } else {
        setKudos([]);
        setKudosByType({});
      }
    } catch (error) {
      console.error('Error fetching kudos:', error);
      setKudos([]);
      setKudosByType({});
    } finally {
      setLoading(false);
      // Trigger celebration if arrived from notification
      triggerCelebration();
    }
  };

  const triggerCelebration = () => {
    const fromNotification = searchParams.get('from') === 'notification';
    const tab = getCurrentTab();
    if (!fromNotification || tab !== 'received') return;

    // Reset and replay every time user clicks the bell
    setIsCelebrating(false);
    setCelebrationMessage(null);
    setHighlightedTypes(new Set());
    setAnimatedCounts({});

    // Small delay so React processes the reset before re-triggering
    setTimeout(() => {
      setIsCelebrating(true);
    }, 50);
  };

  // Build celebration message when celebrating starts and kudos are available
  useEffect(() => {
    if (!isCelebrating || kudos.length === 0) return;

    // Always highlight and celebrate the most recent kudo
    const newest = kudos[0];
    const newestType = new Set([newest.kudosType]);
    setHighlightedTypes(newestType);

    const typeInfo = getKudosTypeById(newest.kudosType);
    if (newest.kudosType === 'other_magic') {
      setCelebrationMessage({
        text: 'Someone sent you a special note of appreciation',
        expandable: true,
        customMessage: newest.customMessage,
      });
    } else {
      setCelebrationMessage({
        text: `You are being seen! Someone sent you a sweet note of appreciation for your ${typeInfo?.label || 'contribution'}`,
      });
    }

    // Animate counts from 0 to actual values
    const targets = {};
    KUDOS_TYPES.forEach(type => {
      targets[type.id] = kudosByType[type.id] || 0;
    });

    const current = {};
    KUDOS_TYPES.forEach(type => { current[type.id] = 0; });
    setAnimatedCounts({ ...current });

    const duration = 1200;
    const steps = 30;
    const interval = duration / steps;
    let step = 0;

    const countTimer = setInterval(() => {
      step++;
      const progress = step / steps;
      const eased = 1 - Math.pow(1 - progress, 3);

      const frame = {};
      KUDOS_TYPES.forEach(type => {
        frame[type.id] = Math.round(eased * targets[type.id]);
      });
      setAnimatedCounts(frame);

      if (step >= steps) {
        clearInterval(countTimer);
        // Stop icon animation after a pause, but keep banner
        setTimeout(() => setIsCelebrating(false), 4000);
      }
    }, interval);

    // Auto-dismiss celebration banner after 1 minute
    celebrationTimerRef.current = setTimeout(() => {
      setCelebrationMessage(null);
    }, 60000);

    return () => {
      clearInterval(countTimer);
      if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
    };
  }, [isCelebrating, kudos, kudosByType]);

  // Remove the notification param after celebration triggers so next bell click is fresh
  useEffect(() => {
    const fromNotification = searchParams.get('from') === 'notification';
    if (!fromNotification || !isCelebrating) return;

    const readTimer = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('from');
      setSearchParams(newParams, { replace: true });
    }, 2000);

    return () => clearTimeout(readTimer);
  }, [isCelebrating]);

  // Check if a kudo is "new" (within 24 hours) for table highlighting
  const isNewKudo = (kudo) => {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return new Date(kudo.createdAt) > oneDayAgo;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getKudosTypeInfo = (typeId) => {
    return getKudosTypeById(typeId);
  };

  const toggleExpand = (kudoId) => {
    setExpandedKudos(expandedKudos === kudoId ? null : kudoId);
  };

  // Get unique events for filter dropdown
  const getUniqueEvents = () => {
    const eventMap = {};
    kudos.forEach(k => {
      if (k.eventId?._id && k.eventId?.title) {
        eventMap[k.eventId._id] = k.eventId.title;
      }
    });
    return Object.entries(eventMap).map(([id, title]) => ({ id, title }));
  };

  // Filter and sort kudos
  const getFilteredKudos = () => {
    let filtered = eventFilter === 'all'
      ? [...kudos]
      : kudos.filter(k => k.eventId?._id === eventFilter);

    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  };

  const handleFlagKudos = async (kudosId) => {
    if (flaggingId) return; // Prevent double-click

    const confirmFlag = window.confirm(
      'Are you sure you want to flag this message as inappropriate? This will be reviewed by our moderation team.'
    );

    if (!confirmFlag) return;

    setFlaggingId(kudosId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/user-kudos/${kudosId}/flag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: 'Inappropriate content' }),
      });

      const data = await res.json();
      if (data.success) {
        // Update local state to show flagged status
        setKudos(prev => prev.map(k =>
          k._id === kudosId ? { ...k, flagged: true } : k
        ));
        alert('Message flagged successfully. Our team will review it.');
      } else {
        alert(data.error || 'Failed to flag message');
      }
    } catch (error) {
      console.error('Error flagging kudos:', error);
      alert('Failed to flag message. Please try again.');
    } finally {
      setFlaggingId(null);
    }
  };

  const renderKudosIcons = () => {
    const tab = getCurrentTab();
    const totalKudos = Object.values(kudosByType).reduce((a, b) => a + b, 0);
    const useAnimated = isCelebrating && Object.keys(animatedCounts).length > 0;
    const animatedTotal = useAnimated
      ? Object.values(animatedCounts).reduce((a, b) => a + b, 0)
      : totalKudos;

    return (
      <div className="kudos-icons-container">
        <div className={`kudos-icons-grid ${isCelebrating ? 'celebrating' : ''}`}>
          {KUDOS_TYPES.map((type) => {
            const count = useAnimated ? (animatedCounts[type.id] || 0) : (kudosByType[type.id] || 0);
            const isHighlighted = highlightedTypes.has(type.id);
            return (
              <div
                key={type.id}
                className={`kudos-icon-item ${isCelebrating ? 'kudos-icon-item--animate' : ''} ${isHighlighted ? 'kudos-icon-item--highlighted' : ''}`}
              >
                <div className={`kudos-icon-wrapper ${isHighlighted ? 'kudos-icon-wrapper--glow' : ''}`}>
                  <img src={type.icon} alt={type.label} />
                  <span className={`kudos-count-badge ${isCelebrating && isHighlighted ? 'kudos-count-badge--pulse' : ''}`}>{count}</span>
                </div>
                <span
                  className="kudos-icon-label"
                  onMouseEnter={() => setHoveredKudosType(type.id)}
                  onMouseLeave={() => setHoveredKudosType(null)}
                >
                  {type.label}
                </span>
              </div>
            );
          })}
          <div className="kudos-total">
            <span className="total-label">Total {tab === 'received' ? 'Received' : 'Given'}</span>
            <span className="total-count">{animatedTotal}</span>
          </div>
        </div>
        {/* Celebration message */}
        {celebrationMessage && (
          <div className="kudos-celebration-banner">
            <span className="celebration-sparkle">&#10024;</span>
            <p className="celebration-text">{celebrationMessage.text}</p>
            {celebrationMessage.expandable && celebrationMessage.customMessage && (
              <p className="celebration-custom-message">"{celebrationMessage.customMessage}"</p>
            )}
          </div>
        )}
        {/* Description bubble - appears when hovering a kudo label */}
        {hoveredKudosType && (
          <KudoDescriptionBubble kudoTypeId={hoveredKudosType} variant="inline" />
        )}
      </div>
    );
  };

  const renderKudosTable = () => {
    const tab = getCurrentTab();

    if (kudos.length === 0) {
      const emptyStates = {
        received: {
          icon: 'bi-gift',
          title: 'No kudos received yet',
          description: 'Kudos you receive from other attendees at events will appear here.',
          tip: 'Attend events and engage with the community to receive kudos!',
        },
        given: {
          icon: 'bi-send-check',
          title: 'No kudos given yet',
          description: 'After attending an event, you can give kudos to special humans you got to connect with.',
          tip: 'Show appreciation for memorable connections at dance events!',
        },
      };

      const state = emptyStates[tab] || emptyStates.received;

      return (
        <div className="empty-state">
          <i className={`bi ${state.icon}`}></i>
          <h3>{state.title}</h3>
          <p>{state.description}</p>
          <p className="tip"><i className="bi bi-lightbulb"></i> {state.tip}</p>
        </div>
      );
    }

    const uniqueEvents = getUniqueEvents();
    const filteredKudos = getFilteredKudos();

    return (
      <div className="kudos-table-container">
        {/* Filters Row */}
        <div className="kudos-filter-row">
          {/* Event Filter */}
          {uniqueEvents.length > 1 && (
            <select
              className="kudos-event-filter"
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
            >
              <option value="all">All Events</option>
              {uniqueEvents.map(event => (
                <option key={event.id} value={event.id}>{event.title}</option>
              ))}
            </select>
          )}
          {/* Date Sort Filter - Icon Toggle */}
          <button
            className={`kudos-date-filter-btn ${sortOrder === 'newest' ? 'active-desc' : 'active-asc'}`}
            onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
            title={sortOrder === 'newest' ? 'Showing newest first (click for oldest)' : 'Showing oldest first (click for newest)'}
          >
            <i className="bi bi-calendar3"></i>
            <span>Date</span>
          </button>
        </div>
        <table className="kudos-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Event</th>
              <th>Date</th>
              {tab === 'given' && <th>To</th>}
            </tr>
          </thead>
          <tbody>
            {filteredKudos.map((kudo, idx) => {
              const typeInfo = getKudosTypeInfo(kudo.kudosType);
              const hasMessage = kudo.kudosType === 'other_magic' && kudo.customMessage;
              const isExpanded = expandedKudos === (kudo._id || idx);
              // Only show message when expanded (user clicks 3 dots)
              const showMessage = hasMessage && isExpanded;

              return (
                <React.Fragment key={kudo._id || idx}>
                  <tr className={`${hasMessage ? 'has-message' : ''} ${isNewKudo(kudo) ? 'kudo-row--new' : ''}`}>
                    <td className="type-cell">
                      <img src={typeInfo.icon} alt={typeInfo.label} className="table-kudos-icon" />
                    </td>
                    <td className="event-cell">
                      <div className="event-cell-content">
                        <span
                          className="event-title clickable"
                          onClick={() => kudo.eventId?._id && navigate(`/event/${kudo.eventId._id}`)}
                        >
                          {kudo.eventId?.title || 'Event'}
                        </span>
                        {hasMessage && (
                          <button
                            className="expand-btn"
                            onClick={() => toggleExpand(kudo._id || idx)}
                            title={isExpanded ? 'Hide message' : 'View message'}
                          >
                            <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-three-dots'}`}></i>
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="date-cell">
                      {formatDate(kudo.createdAt)}
                    </td>
                    {tab === 'given' && (
                      <td className="user-cell">
                        <div className="user-info-mini">
                          {kudo.toUserId?.profilePicture ? (
                            <img src={kudo.toUserId.profilePicture} alt="" className="user-avatar-mini" />
                          ) : (
                            <div className="avatar-placeholder-mini">
                              {(kudo.toUserId?.fullName || 'A').charAt(0)}
                            </div>
                          )}
                          <span>{kudo.toUserId?.fullName || 'Anonymous'}</span>
                        </div>
                      </td>
                    )}
                  </tr>
                  {showMessage && (
                    <tr className="message-row">
                      <td colSpan={tab === 'given' ? 4 : 3}>
                        <div className="kudos-message-expanded">
                          <div className="message-content">
                            <i className="bi bi-chat-quote"></i>
                            <span>"{kudo.customMessage}"</span>
                          </div>
                          {kudo.flagged ? (
                            <span className="flagged-badge">
                              <i className="bi bi-flag-fill"></i> Flagged
                            </span>
                          ) : tab === 'received' && (
                            <button
                              className="flag-message-btn"
                              onClick={() => handleFlagKudos(kudo._id)}
                              disabled={flaggingId === kudo._id}
                              title="Flag as inappropriate"
                            >
                              <i className={`bi ${flaggingId === kudo._id ? 'bi-hourglass-split' : 'bi-flag'}`}></i>
                              <span>{flaggingId === kudo._id ? 'Flagging...' : 'Flag'}</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const getTabTitle = () => {
    const tab = getCurrentTab();
    const titles = {
      received: 'Kudos Received',
      given: 'Kudos Given',
    };
    return titles[tab] || 'My Kudos';
  };

  const getTabDescription = () => {
    const tab = getCurrentTab();
    const descriptions = {
      received: 'Recognition and appreciation from fellow dancers',
      given: 'Kudos you\'ve shared with others in the community',
    };
    return descriptions[tab] || '';
  };

  if (loading) {
    return (
      <div className="section-content kudos-section">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading kudos...</p>
        </div>
      </div>
    );
  }

  const tab = getCurrentTab();

  return (
    <div className="section-content kudos-section">
      <div className="section-header">
        <h1>{getTabTitle()}</h1>
        <p className="section-subtitle">{getTabDescription()}</p>
      </div>

      {/* Kudos Icons with Counts */}
      {renderKudosIcons()}

      {/* Kudos History Section */}
      <div className="kudos-history">
        <h3 className="history-title">
          <i className="bi bi-clock-history"></i> History
        </h3>
        {renderKudosTable()}
      </div>

      {/* Find Events Button - Bottom Right for "given" tab */}
      {tab === 'given' && (
        <div className="section-footer-action">
          <button className="btn-find-events" onClick={() => navigate('/events')}>
            <i className="bi bi-calendar-event"></i> Find Events
          </button>
        </div>
      )}
    </div>
  );
};

export default KudosSection;
